import React, {useContext, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {AppContext} from "../authProvider/AuthProvider";
import {useNavigate} from "react-router";
import {UiInput} from "../ui-components/input/uiInput";
import {UiSelect} from "../ui-components/select/uiSelect";
import {UiOption} from "../ui-components/select/option/uiOption";
import countries from "../../data/countries.json"
import {ButtonType, UiButton} from "../ui-components/button/uiButton";
import {TokenManager} from "../../services/TokenManager";
import {EditProfilePicture} from "./editProfilePicture/editProfilePicture";
import {UserApi} from "../../services/api/UserApi";
import {ToastType} from "../ui-components/toast/UiToast";
import {UiPopup} from "../ui-components/popup/UiPopup";
import {UiCropImage} from "../ui-components/crop-image/UiCropImage";
import {ChangePassword} from "./changePassword/changePassword";
import {API_URL, LOGIN_MAX_LENGTH, LOGIN_MIN_LENGTH} from "../../constants/CONST";
import {UiFlag} from "../ui-components/flag/UiFlag";


export const Account: React.FC = () => {
    const {

        isConnected, setIsConnected, currentUser, setUser, openToastWithParams, errorToast,
    } = useContext(AppContext);
    const {t} = useTranslation();
    const navigate = useNavigate();

    const [isChangePasswordPopupOpen, setIsChangePasswordPopupOpen] = useState(false);
    const [changePasswordIsLoading, setChangePasswordIsLoading] = useState(false)

    const [changePasswordErrorMessage, setChangePasswordErrorMessage] = useState<string>("");
    const [changeLoginErrorMessage, setChangeLoginErrorMessage] = useState<string>("");

    const [isCropImagePopupOpen, setIsCropImagePopupOpen] = useState(false);
    const [profilePictureUrl, setProfilePictureUrl] = useState<string>("");
    const [previewProfilePictureUrl, setPreviewProfilePictureUrl] = useState<string>("");
    const ppInputFile = useRef<HTMLInputElement>(null);

    const [editionMode, setEditionMode] = useState<boolean>(false);
    const [newData, setNewData] = useState<{ name: string, country: string, pp: File | undefined }>({
        name: currentUser?.name ?? "", country: currentUser?.country ?? "", pp: undefined
    });


    function openToast(isSuccess: boolean, text: string) {
        openToastWithParams({
            message: text, type: isSuccess ? ToastType.success : ToastType.error, duration: 5
        })
    }

    useEffect(function init() {
        if (!isConnected || currentUser === null) {
            navigate("/login");
        }

        setProfilePictureUrl(getProfilePictureUrl())
    }, []);

    function getProfilePictureUrl(): string {
        if (currentUser === null) {
            return "";
        }

        return `${API_URL}/users/profilePicture/${currentUser!.id}?${Date.now()}`;
    }

    async function updateName(newName: string) {
        if (currentUser === null || newName === currentUser.name) {
            return false;
        }

        if (newName.length < LOGIN_MIN_LENGTH) {
            setChangeLoginErrorMessage(t("account.username-min-characters", {number: LOGIN_MIN_LENGTH}))
            return false;
        }

        if (newName.length > LOGIN_MAX_LENGTH) {
            setChangeLoginErrorMessage(t("account.username-max-characters", {number: LOGIN_MAX_LENGTH}))
            return false;
        }

        const isUpdated = await UserApi.updateUserName(newName)

        if (!isUpdated) {
            return false;
        }

        setUser(prevState => {
            if (prevState === null) {
                return null;
            }

            prevState.name = newName;
            return prevState;
        })

        return true;
    }

    async function updateProfilePicture(file: File) {
        const success = await UserApi.updateProfilePicture(file)

        if (success) {
            setProfilePictureUrl(getProfilePictureUrl())
        }

        ppInputFile.current!.value = "";
        return success;
    }

    async function updateCountry(newCountry: string) {
        if (currentUser === null || newCountry === currentUser.country) {
            return false;
        }

        const isUpdated = await UserApi.updateCountry(newCountry)

        if (!isUpdated) {
            return false;
        }

        setUser(prevState => {
            if (prevState === null) {
                return null;
            }

            prevState.country = newCountry;
            return prevState;
        })

        return true;
    }

    function changePassword(actualPassword: string, newPassword: string) {
        setChangePasswordIsLoading(true)
        setChangePasswordErrorMessage("")

        UserApi.updatePassword(actualPassword, newPassword)
            .then((res) => {
                if (res === null) {
                    openToast(false, t("error.error-occured"))
                    return;
                }

                const [passwordIsChange, status] = res;

                if (!passwordIsChange) {
                    switch (status) {
                        case 401:
                            setChangePasswordErrorMessage(t("form.incorrect-password"))
                            break;
                        default:
                            openToast(false, t("error.error-occured"))
                    }
                } else {
                    openToast(true, t("account.password-updated"))
                    setIsChangePasswordPopupOpen(false)
                    setEditionMode(false)
                }
            })
            .catch(() => errorToast())
            .finally(() => {
                setChangePasswordIsLoading(false)
            })
    }

    async function saveChanges() {
        let isNameUpdated = true;
        let isCountryUpdated = true;
        let ppUpdated = true;
        const failedFields: string[] = [];

        if (newData.name && newData.name !== currentUser?.name) {
            isNameUpdated = await updateName(newData.name);

            if (isNameUpdated) {
                setChangeLoginErrorMessage("")
            } else {
                failedFields.push(t("account.name"));
            }
        }

        if (newData.country && newData.country !== currentUser?.country) {
            isCountryUpdated = await updateCountry(newData.country);

            if (!isCountryUpdated) {
                failedFields.push(t("form.country"));
            }
        }

        if (newData.pp) {
            ppUpdated = await updateProfilePicture(newData.pp);

            if (!ppUpdated) {
                failedFields.push(t("account.profile-picture"));
            }
        }

        if (isNameUpdated && isCountryUpdated && ppUpdated) {
            openToast(true, t("account.data-updated"))
            setEditionMode(false);
            setPreviewProfilePictureUrl("");
        } else {
            const failedFieldsText = failedFields.join(", ");
            openToast(false, `${t("account.failed-update")}: ${failedFieldsText}`)
        }
    }

    function disconnect() {
        TokenManager.deleteToken();
        setIsConnected(false);
        setUser(null);
        navigate("/")
    }

    return (<>
        <UiPopup
            className="p-5 bg-deep-blue w-3/5 h-3/5"
            isOpen={isCropImagePopupOpen}
            onClose={() => setIsCropImagePopupOpen(false)}
        >
            <UiCropImage
                file={ppInputFile.current?.files![0]}
                cancelFunc={() => {
                    if (ppInputFile.current) {
                        ppInputFile.current!.value = ""
                    }

                    setIsCropImagePopupOpen(false)
                }}
                confirmFunc={(croppedFile: File) => {
                    setNewData(prevState => {
                        return {...prevState, pp: croppedFile}
                    });

                    // Create a local preview url
                    const previewUrl = URL.createObjectURL(croppedFile);
                    setPreviewProfilePictureUrl(previewUrl);

                    setIsCropImagePopupOpen(false);
                }}
            />
        </UiPopup>

        <UiPopup
            className={"p-5 bg-darkblue w-1/5 min-w-60 h-2/5 min-h-36"}
            isOpen={isChangePasswordPopupOpen}
            onClose={() => setIsCropImagePopupOpen(false)}
        >
            <ChangePassword
                confirmFunc={changePassword}
                cancelFunc={() => setIsChangePasswordPopupOpen(false)}
                errorMessage={changePasswordErrorMessage}
                setErrorMessage={setChangePasswordErrorMessage}
                isLoading={changePasswordIsLoading}
            />
        </UiPopup>

        <input
            className="hidden"
            type="file"
            ref={ppInputFile}
            onChange={() => setIsCropImagePopupOpen(true)}
            accept="image/*"
        />

        {(isConnected && currentUser !== null) &&
            <div className="px-12 md:px-28 xl:px-44 pt-20 pb-12 md:pt-28 xl:pt-30 gap-8 h-full justify-between flex flex-col overflow-y-auto">
                <h1 className="text-4xl">{t("global.account")}</h1>

                <div className="flex flex-col lg:flex-row gap-8 lg:justify-between justify-center lg:items-center">
                    <div className="flex flex-wrap gap-6 items-center justify-center">
                        {/* Profile picture */}
                        <EditProfilePicture
                            profilePictureUrl={profilePictureUrl}
                            previewProfilePictureUrl={previewProfilePictureUrl}
                            onEditClick={() => {
                                if (ppInputFile.current === null) {
                                    return;
                                }

                                ppInputFile.current.click();
                            }}
                            editMode={editionMode}
                        />

                        {!editionMode && <div className="flex flex-wrap flex-col gap-6 items-center justify-center">
                            {/* Username and join date */}
                            <div className="flex flex-col gap-2 items-center">
                                <div className="flex items-center gap-2">
                                    <UiFlag className="h-6 relative" countryCode={currentUser.country!}/>
                                    <p className="text-3xl">{currentUser.name}</p>
                                </div>

                                <p className="text-text-transparent">{t("stats.joined-on-x", {date: currentUser.createdAt})}</p>
                            </div>
                        </div>}
                    </div>

                    <div className="flex flex-wrap gap-8 justify-center">
                        {editionMode && <div className="flex flex-col gap-8">
                            {/* Username */}
                            <div>
                                <p className="text-xl">{t("account.name")}</p>

                                <UiInput
                                    className="w-60 mt-2"
                                    placeholder={t("global.username")}
                                    defaultValue={currentUser.name}
                                    enterKeyTriggerBlurInput={true}
                                    enterKeyTriggerIconFunction={true}
                                    onChange={(event) => {
                                        setChangeLoginErrorMessage("")
                                        setNewData(prevState => {
                                            return {...prevState, name: (event.target as HTMLInputElement).value}
                                        })
                                    }}
                                />

                                {changeLoginErrorMessage !== "" &&
                                    <p className="text-danger w-60">{changeLoginErrorMessage}</p>}
                            </div>

                            {/* Country */}
                            <div>
                                <p className="text-xl">{t("form.country")}</p>

                                <UiSelect
                                    className="h-12 mt-2 w-60"
                                    onChange={(value) => {
                                        setNewData(prevState => {
                                            return {...prevState, country: value}
                                        })
                                    }}
                                    defaultValue={currentUser.country}
                                    onOpenClearSearch={true}
                                >
                                    {countries.map((countryCode) => {
                                        return (<UiOption
                                            value={countryCode}
                                            key={countryCode}
                                            searchData={t(`countries.${countryCode}`)}
                                        >
                                            <div className="flex gap-3 items-center">
                                                <UiFlag
                                                    className="min-w-8 h-5"
                                                    countryCode={countryCode}
                                                    showTooltip={false}
                                                />
                                                <p>{t(`countries.${countryCode}`)}</p>
                                            </div>
                                        </UiOption>)
                                    })}
                                </UiSelect>
                            </div>
                        </div>}

                        <div className="flex flex-col gap-8">
                            {/* Email */}
                            <div className="flex flex-col items-start gap-2 w-60">
                                <p className="text-xl">{t("form.email")}</p>

                                <UiInput
                                    className="w-full"
                                    placeholder={currentUser.email}
                                    disabled={true}
                                />
                            </div>

                            {/* Password */}
                            <div className="flex flex-col gap-2 w-60">
                                <p className="text-xl">{t("form.password")}</p>

                                {/* display the edit icon on edit mode */}
                                {!editionMode ? <UiInput
                                    name="password"
                                    placeholder={"*".repeat(15)}
                                    disabled={true}
                                /> : <UiInput
                                    name="password"
                                    placeholder={"*".repeat(15)}
                                    disabled={true}
                                    icon="edit"
                                    iconFunction={() => setIsChangePasswordPopupOpen(true)}
                                />}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Actions buttons */}
                {editionMode ? <div className="flex flex-col items-center gap-4">
                    <UiButton
                        className="!w-60"
                        text={t("button.save")}
                        onClick={saveChanges}
                    />

                    <UiButton
                        className="!w-60"
                        text={t("button.cancel")}
                        type={ButtonType.transparentBg}
                        onClick={() => {
                            setEditionMode(false);
                            setPreviewProfilePictureUrl("");
                        }}
                    />
                </div> : <div className="flex flex-col items-center gap-4">
                    <UiButton
                        className="!w-60"
                        text={t("button.edition-mode")}
                        onClick={() => setEditionMode(true)}
                    />

                    <UiButton
                        className="!w-60"
                        type={ButtonType.danger}
                        text={t("account.disconnect")}
                        onClick={disconnect}
                    />
                </div>}
            </div>}
    </>)
}
